import React, { useEffect, useState, useContext, useMemo, useCallback, useRef } from 'react';
import { useTranslation } from "react-i18next";
import '../pages.css';
import {
    MButton, LoadingContext, M_StateActions,
    MHeader, MInputTextField, NavigateContext
} from 'src/M_Components';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { BufferService } from 'src/M_Components/M_Utils/buffer.service';
import { isFailurePayload, apiMessage } from '../../components/utils/apiResponse';
import DynamicForm from '../../components/Forms/DynamicForm';

// =======================================================================================================================================================
// Helpers and Rules
// =======================================================================================================================================================

// Converts a string to camelCase (used to build DTO payload keys from Option.Header/Name).
const toCamel = (s) => (s ? s.charAt(0).toLowerCase() + s.slice(1) : s);

export default function CarrierLoginsConfigCrud({ handleTabClose, toastMessages }) {
    const dispatch = useDispatch();
    const ctx = useContext(LoadingContext);
    const M_State = useSelector((state) => state.StateKeyValue);
    const { t, i18n } = useTranslation();
    const { renameTab } = useContext(NavigateContext);
    const selectedTab = M_State.find(o => o.key === 'selectedTab')?.value;

    // Global “one-shot” data set by the list page before opening the CRUD tab
    const globalOperation = M_State.find(obj => obj.key === 'operation')?.value;
    const globalLine = M_State.find(obj => obj.key === 'CarrierLoginLine')?.value;

    // =====================================================================================================================================================
    // Per-tab state (kept isolated by selectedTab id key)
    // =====================================================================================================================================================
    const [id, setId] = useState(M_State.find(obj => obj.key === 'selectedTab')?.value);
    const [operation, setOperation] = useState(globalOperation ?? 'Error');
    const [sessionLine, setSessionLine] = useState(globalLine ?? {});
    const [loginInfo, setLoginInfo] = useState({});
    const [isFirst, setIsFirst] = useState(true);
    const [loginId, setLoginId] = useState(sessionLine?.Id);

    // Options for DynamicForm (no FK fetching here; DynamicForm handles it)
    const [allOptions, setAllOptions] = useState([]); // Raw Options for DynamicForm

    // Imperative ref to DynamicForm (submit/getValues/reset)
    const formRef = useRef(null);

    // Stale-closure guards
    const idRef = useRef(id);
    const operationRef = useRef(operation);
    const loginIdRef = useRef(loginId);
    useEffect(() => { idRef.current = id; }, [id]);
    useEffect(() => { operationRef.current = operation; }, [operation]);
    useEffect(() => { loginIdRef.current = loginId; }, [loginId]);

    // Async race guard between tab switches
    const tabTokenRef = useRef(0);
    useEffect(() => { tabTokenRef.current += 1; }, [id]);

    const isViewMode = (operation || '').toLowerCase() === 'view';

    // BufferServices (explicit baseUrl to avoid hooks inside constructor)
    // Endpoints (per your Swagger):
    //   GET    /api/CarrierLogins
    //   GET    /api/CarrierLogins/{id}
    //   POST   /api/CarrierLogins/create
    //   PUT    /api/CarrierLogins/update/{id}
    //   DELETE /api/CarrierLogins/{id}
    const svcLogins = useMemo(() => new BufferService('CarrierLogins', ctx.DEFAULTURL), [ctx.DEFAULTURL]);
    const svcCreate = useMemo(() => new BufferService('CarrierLogins/Create', ctx.DEFAULTURL), [ctx.DEFAULTURL]);
    const svcUpdate = useMemo(() => new BufferService('CarrierLogins/Update', ctx.DEFAULTURL), [ctx.DEFAULTURL]);

    // =====================================================================================================================================================
    // EFFECT
    // Update tab title on operation or entity username change
    // =====================================================================================================================================================
    useEffect(() => {
        if (!selectedTab || !operation) return;

        // Determine the i18n key (label) based on the operation
        const i18nKey =
            operation === 'Create' ? 'LabelNewCarrierLogin' :
                operation === 'Edit' ? 'LabelEditCarrierLogin' :
                    'LabelViewCarrierLogin';

        // Try username from session line first, then from entity details
        const uname = sessionLine?.UserName ?? loginInfo?.UserName ?? '';
        const base = t(i18nKey);
        const label = (operation === 'Create' || !uname) ? base : `${base} "${uname}"`;
        renameTab(String(selectedTab), label);
    }, [selectedTab, operation, sessionLine?.UserName, loginInfo?.UserName]);

    // =====================================================================================================================================================
    // EFFECT
    // Initialize state from session storage on first render
    // =====================================================================================================================================================
    useEffect(() => {
        // Restore session data for this tab
        const sessionForThisTab = M_State.find(o => o.key === id)?.value || [];
        const pick = (k) => sessionForThisTab.find(x => x.key === k)?.value;

        const sessionOp = pick('operation');
        const sessionSL = pick('sessionLine');

        // restore create/edit form state so it does not get wiped on remount
        const sessionInfo = pick('loginInfo');
        const sessionIF = pick('isFirst');
        const sessionOptions = pick('allOptions') ?? pick('formSchema'); // backward compatibility
        const sessionLoginId = pick('loginId');

        const seedOperation = (globalOperation !== undefined ? globalOperation : sessionOp);
        const seedLine = (globalLine !== undefined ? globalLine : sessionSL);

        if (seedOperation !== undefined) setOperation(seedOperation);
        if (seedLine) {
            setSessionLine(seedLine);
            if (seedLine.Id) setLoginId(seedLine.Id);
        } else if (sessionLoginId) {
            setLoginId(sessionLoginId);
        }

        if (sessionInfo) setLoginInfo(sessionInfo);
        if (sessionIF !== undefined) setIsFirst(sessionIF);
        else if (isFirst) setIsFirst(false);
        if (Array.isArray(sessionOptions)) setAllOptions(sessionOptions);

        // consume one-shot globals
        if (globalOperation !== undefined) dispatch(M_StateActions.removeM_State({ key: 'operation' }));
        if (globalLine !== undefined) dispatch(M_StateActions.removeM_State({ key: 'CarrierLoginLine' }));

        // Fetch Options first; details as needed
        fetchOptions(seedOperation, seedLine, { skipDetails: true }).finally(async () => {
            if (seedOperation && seedOperation !== 'Create' && seedLine?.Id) {
                await fetchDetails(seedLine?.Id);
                ctx.setIsLoad(false);
            } else {
                ctx.setIsLoad(false);
            }
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Detects audit columns to hide
    const isAudit = (name) => ["CreatedAt", "UpdatedAt", "CreatedBy", "UpdatedBy"].includes(name);


    // =====================================================================================================================================================
    // Dont include in Create
    // =====================================================================================================================================================
    const IncludeInCreate = (opt) => {
        if (!opt) return false;
        if (opt.IsKey || isAudit(opt.Name)) return false;
        if (opt.IsHiddenEdit === true) return false;
        if (opt.Name === "LockoutEndUtc") return false;
        if (opt.Name === "Inactive") return false;
        return true;
    };

    // =====================================================================================================================================================
    // Dont include in Edtit
    // =====================================================================================================================================================
    const IncludeInEdit = (opt) => {
        if (!opt) return false;
        if (opt.IsKey || isAudit(opt.Name)) return false;
        if (opt.Name === "Carrier") return false;
        if (opt.Name === "UserName") return false;
        if (opt.IsHiddenEdit === true) return false;
        return true;
    };

    // =====================================================================================================================================================
    // EFFECT
    // Persist per-tab snapshot (debounced)
    // =====================================================================================================================================================
    const saveSnapshotTimer = useRef(null);
    useEffect(() => {
        if (!id) return;

        // Merge current form values so unsaved edits persist across tabs
        const currentFormValues = formRef.current?.getValues?.() || {};
        const infoToPersist =
            (operation || '').toLowerCase() === 'view'
                ? loginInfo
                : { ...loginInfo, ...currentFormValues };

        const sessionData = [
            { key: 'loginInfo', value: infoToPersist },
            { key: 'isFirst', value: isFirst },
            { key: 'operation', value: operation },
            { key: 'sessionLine', value: sessionLine },
            { key: 'allOptions', value: allOptions },
            { key: 'loginId', value: loginId },
        ];

        if (saveSnapshotTimer.current) clearTimeout(saveSnapshotTimer.current);
        saveSnapshotTimer.current = setTimeout(() => {
            dispatch(M_StateActions.addM_State({ key: id, value: sessionData }));
        }, 80);

        return () => {
            if (saveSnapshotTimer.current) clearTimeout(saveSnapshotTimer.current);
        };
    }, [id, loginInfo, isFirst, operation, sessionLine, allOptions, loginId, dispatch]);

    // =====================================================================================================================================================
    // EFFECT
    // Track tab switch -->> restore that tab snapshot or refetch
    // =====================================================================================================================================================
    useEffect(() => {
        const newId = M_State.find(obj => obj.key === 'selectedTab')?.value;
        if (newId && newId !== id) {
            setId(newId);
            getSessionStorageData(newId);
        }
    }, [M_State, id]); // keep

    // getSessionStorageData
    // Restores a tab snapshot if available; otherwise fetches options and details as needed.
    const getSessionStorageData = (sessionID) => {
        ctx.setIsLoad(true);
        const data = M_State.find(obj => obj.key === sessionID)?.value;
        if (data) {
            const pick = (k) => data.find((x) => x.key === k)?.value;
            const tempInfo = pick('loginInfo');
            const tempIF = pick('isFirst');
            const tempOP = pick('operation');
            const tempSL = pick('sessionLine');
            const tempOptions = pick('allOptions') ?? pick('formSchema'); // backward compatibility
            const tempLoginId = pick('loginId');

            setLoginInfo(tempInfo || {});
            setIsFirst(tempIF ?? false);
            setOperation(tempOP ?? 'Error');
            setSessionLine(tempSL || {});
            if (tempSL?.Id) setLoginId(tempSL.Id);
            else if (tempLoginId) setLoginId(tempLoginId);
            if (Array.isArray(tempOptions)) setAllOptions(tempOptions);

            ctx.setIsLoad(false);
            return;
        }
        // No snapshot : refetch
        fetchOptions(operation, sessionLine, { skipDetails: true }).finally(async () => {
            if (operation !== 'Create' && sessionLine?.Id) await fetchDetails(sessionLine?.Id);
            ctx.setIsLoad(false);
        });
    };

    // =====================================================================================================================================================
    // EFFECT
    // React to language changes: re-fetch Options so labels reflect current language
    // =====================================================================================================================================================
    useEffect(() => {
        if (!operation || isFirst) return; // skip first render or no operation set
        fetchOptions(operation, sessionLine, { skipDetails: true });

        // Update tab title on language change
        const i18nKey =
            operation === 'Create' ? 'LabelNewCarrierLogin' :
                operation === 'Edit' ? 'LabelEditCarrierLogin' :
                    'LabelViewCarrierLogin';

        const uname = sessionLine?.UserName ?? loginInfo?.UserName ?? '';
        const base = t(i18nKey);
        const label = (operation === 'Create' || !uname) ? base : `${base} "${uname}"`;
        renameTab(String(selectedTab), label);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [i18n.language]);

    // =====================================================================================================================================================
    // fetchOptions
    // Fetches raw Options for CarrierLogins and optionally details for Edit/View.
    // NOTE: FK lists are NOT fetched here; DynamicForm handles FK sources internally.
    // =====================================================================================================================================================
    const fetchOptions = async (op, line, { skipDetails } = { skipDetails: false }) => {
        const myToken = tabTokenRef.current;
        ctx.setIsLoad(true);
        try {
            const res = await svcLogins.options(); // /api/Options for CarrierLogins
            if (isFailurePayload(res) || res?.name === 'AxiosError') {
                toastMessages('error', '', apiMessage(res, t('ErrorLoadingData')));
                ctx.setIsLoad(false);
                return;
            }

            if (myToken !== tabTokenRef.current) return; // drop late updates after tab switch
            setAllOptions(res || []);

            const mode = op || operationRef.current || 'View';
            if (!skipDetails && mode !== 'Create' && (line?.Id || sessionLine?.Id)) {
                await fetchDetails(line?.Id || sessionLine?.Id);
            }
        } catch (err) {
            toastMessages('error', '', apiMessage(err, t('ErrorLoadingData')));
        } finally {
            if (myToken === tabTokenRef.current) ctx.setIsLoad(false);
        }
    };

    // =====================================================================================================================================================
    // Fetch entity details (Edit/View)
    // =====================================================================================================================================================
    const fetchDetails = async (idToGet) => {
        const myToken = tabTokenRef.current;
        ctx.setIsLoad(true);
        try {
            const res = await svcLogins.getId(idToGet);
            if (isFailurePayload(res)) {
                toastMessages('error', '', apiMessage(res, t('ErrorLoadingData')));
                return;
            }
            if (myToken !== tabTokenRef.current) return;
            setLoginInfo(res || {});
            setLoginId(res?.Id);
        } catch (err) {
            toastMessages('error', '', apiMessage(err, t('ErrorLoadingData')));
        } finally {
            if (myToken === tabTokenRef.current) ctx.setIsLoad(false);
        }
    };

    // =====================================================================================================================================================
    // buildPayloadFromValues
    // Builds DTO payload from values emitted by DynamicForm and raw options.
    // Steps:
    // 1) Iterate options to determine the correct key source (Header for FK, Name otherwise).
    // 2) Coerce booleans; leave other scalars as-is. FK values already come as ids.
    // 3) Lower camel case keys for DTO and assemble object.
    // =====================================================================================================================================================
    const buildPayloadFromValues = (values) => {
        const options = Array.isArray(allOptions) ? allOptions : [];
        const entries = [];

        for (const opt of options) {
            if (!opt) continue;

            const keySource = opt.IsForeignKey ? (opt.Header || opt.Name) : opt.Name;
            const payloadKey = toCamel(opt.Header || keySource);

            let v;
            if (opt.Type === 'boolean' || opt.Type === 'bool') {
                v = !!values?.[opt.Name];
            } else {
                v = values?.[keySource];
            }

            if (v !== undefined) entries.push([payloadKey, v]);
        }

        return Object.fromEntries(entries);
    };

    // =====================================================================================================================================================
    // onSubmitDynamic
    // Receives values from DynamicForm and performs create/update flow.
    // Steps:
    // 1) Build DTO payload from form values (no transfer lists here).
    // 2) Call create or update service.
    // 3) Toast result, clear per-tab snapshot, and close the tab.
    // IMPORTANT:
    //   - On success we CLOSE THE TAB and RETURN immediately.
    //   - We also REMOVE the tab-specific snapshot from Redux (M_State) so the tab does not reopen
    //     with a stale or half-edited form.
    // =====================================================================================================================================================
    const onSubmitDynamic = useCallback(
        async (values) => {
            // Turn ON global loading spinner for this submit action
            ctx.setIsLoad(true);

            try {
                // 1) Build DTO payload from current DynamicForm values and schema options
                const payload = buildPayloadFromValues(values);

                // Resolve current operation and entity id from refs (avoids stale closures)
                const op = (operationRef.current || '').toLowerCase();   // "create" | "edit"
                const currentId = loginIdRef.current;                    // id of CarrierLogin in edit mode
                const tabId = idRef.current;                             // current tab id (namespace key)

                // 2) Call the appropriate service based on the operation
                const res =
                    op === 'create'
                        ? await svcCreate.post(payload)                  // POST /CarrierLogins/Create
                        : await svcUpdate.update(currentId, payload);    // PUT  /CarrierLogins/Update/{id}

                // 3) Handle API error payload
                if (isFailurePayload(res)) {
                    toastMessages('error', '', apiMessage(res, t('ErrorSaving')));
                    return; // abort submit (finally{} will still reset loading flag)
                }

                // 4) Success toast (different message for create vs edit)
                toastMessages(
                    'success',
                    '',
                    op === 'create'
                        ? t('CarrierLoginCreated')
                        : t('CarrierLoginUpdated')
                );

                // 5) Clear per-tab snapshot from Redux so this tab does not reopen with stale data
                //    - This is similar to clearing "session" for this tab id.
                if (tabId) {
                    dispatch(M_StateActions.removeM_State({ key: tabId }));
                }

                // 6) Close this CRUD tab
                //    - Parent component is responsible for actually removing the tab from the UI.
                handleTabClose(tabId);

                // 7) CRITICAL: return here so no additional state updates run after closing the tab.
                //    - This avoids any re-render of this component with a blank/empty form after close.
                return;
            } catch (err) {
                // Any unexpected error during submit → generic error toast
                toastMessages('error', '', apiMessage(err, t('ErrorSaving')));
            } finally {
                // Always executed (success, failure, or early return inside try):
                // - Turn OFF global loading spinner for this submit action.
                ctx.setIsLoad(false);
            }
        },
        [
            buildPayloadFromValues, // function to translate DynamicForm values -> DTO payload
            dispatch,
            handleTabClose,
            svcCreate,
            svcUpdate,
            toastMessages
        ]
    );


    // Trigger DynamicForm submit via imperative ref
    const triggerFormSubmit = () => {
        formRef.current?.submit?.();
    };

    // Re-fetch options and details if applicable for current mode
    const onRefresh = () => {
        const op = operationRef.current;
        fetchOptions(op, sessionLine);
    };

    // Persist incremental form edits into the per-tab snapshot
    const handleFormValuesChange = useCallback(
        (patch, meta) => {
            if (!patch || !idRef.current) return;

            const prevTabState = M_State.find((o) => o.key === idRef.current)?.value || [];
            const pick = (k) => prevTabState.find((x) => x.key === k)?.value;
            const merged = { ...(pick('loginInfo') || loginInfo || {}), ...patch };

            const updated = [
                ...prevTabState.filter((x) => x.key !== 'loginInfo'),
                { key: 'loginInfo', value: merged }
            ];
            dispatch(M_StateActions.addM_State({ key: idRef.current, value: updated }));
        },
        [M_State, loginInfo, dispatch]
    );

    // =====================================================================================================================================================
    // Render
    // =====================================================================================================================================================
    return (
        <>
            <MHeader element={
                <>
                    <MButton
                        tooltip={t("RefreshTable")}
                        icon={<i className="pi pi-refresh iconTable"></i>}
                        onClick={onRefresh}
                        label={t("RefreshTable")}
                    />
                    {!isViewMode && (
                        <MButton
                            tooltip={t("Save")}
                            icon={<i className="pi pi-save iconTable"></i>}
                            onClick={triggerFormSubmit}
                            label={t("Save")}
                        />
                    )}
                </>
            } />

            <div style={{ padding: '20px', background: 'var(--color-white)' }}>
                <div className='firstDivContainer'>

                    {/* DynamicForm card */}
                    <DynamicForm
                        key={`df-carrier-login-${id}-${operation}-${loginId ?? 'new'}`}
                        formRef={formRef}
                        mode={(operation || 'View').toLowerCase()}
                        schema={allOptions}
                        data={loginInfo}
                        onSubmit={onSubmitDynamic}
                        onValuesChange={handleFormValuesChange}
                        title={t("CarrierLoginInformation")}
                        includeInCreate={IncludeInCreate}
                        includeInEdit={IncludeInEdit}
                    />
                </div>
            </div>
        </>
    );
}

CarrierLoginsConfigCrud.propTypes = {
    handleTabClose: PropTypes.func,
    toastMessages: PropTypes.func
};
